import { useEffect, useState, type ReactNode } from 'react'
import { formatRemaining } from '../lib/format'
import { Logo } from './Logo'

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'required' | 'done' | 'warn' | 'danger'
  children: ReactNode
}) {
  const tones = {
    neutral: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
    required: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    done: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    warn: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
    danger: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  }
  return <span className={`badge ${tones[tone]}`}>{children}</span>
}

/** Re-renders each minute so the deadline stays honest without a page reload. */
export function Countdown({ iso, label }: { iso: string; label: string }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30_000)
    return () => clearInterval(id)
  }, [])

  const remaining = formatRemaining(iso)
  const passed = remaining === 'passed'
  return (
    <span className={passed ? 'text-red-600 dark:text-red-400' : undefined}>
      {label} {passed ? 'passed' : remaining}
    </span>
  )
}

export function ProgressBar({ value, tone = 'brand' }: { value: number; tone?: 'brand' | 'danger' }) {
  const pct = Math.max(0, Math.min(100, value * 100))
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
      <div
        className={`h-full rounded-full transition-[width] duration-200 ${
          tone === 'danger' ? 'bg-red-500' : 'bg-brand-500'
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

export function Spinner({ className = 'size-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2Z" />
    </svg>
  )
}

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'error' | 'success'
  title?: string
  children?: ReactNode
}) {
  const tones = {
    info: 'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-700/50 dark:bg-brand-700/10 dark:text-brand-200',
    warn: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200',
    error: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200',
    success:
      'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200',
  }
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm ${tones[tone]}`}>
      {title && <div className="font-semibold">{title}</div>}
      {children && <div className={title ? 'mt-1' : undefined}>{children}</div>}
    </div>
  )
}

/** Copy-to-clipboard field used for the two generated links. */
export function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Clipboard needs a secure context; the input stays selectable regardless.
      return
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div>
      <span className="label">{label}</span>
      <div className="flex gap-2">
        <input readOnly value={value} onFocus={(e) => e.currentTarget.select()} className="input font-mono text-xs" />
        <button type="button" onClick={copy} className="btn-ghost shrink-0">
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 px-4 py-8 text-center dark:border-zinc-700">
      <div className="text-sm font-medium text-zinc-600 dark:text-zinc-400">{title}</div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
    </div>
  )
}

export function PageShell({ children }: { children: ReactNode }) {
  return (
    <>
      {/* The logo is deliberately not a link. On the recipient page "/" is the
          owner dashboard, which a recipient has no business landing on. */}
      <header className="border-b border-zinc-200 dark:border-zinc-800">
        <div className="mx-auto flex max-w-3xl items-center px-4 py-3.5 sm:px-6">
          <Logo className="text-zinc-900 dark:text-zinc-100" />
        </div>
      </header>
      <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">{children}</div>
    </>
  )
}
